import { Loader2, Play } from 'lucide-react';
import DashboardFilters from './DashboardFilters';
import ScorecardsRow from './ScorecardsRow';
import ProductPerformanceChart from './ProductPerformanceChart';
import CheckinHeatmap from './CheckinHeatmap';
import SessionHeatmap from './SessionHeatmap';
import CheckinPerSessionHeatmap from './CheckinPerSessionHeatmap';
import RatingDistributionTable from './RatingDistributionTable';
import NegativeFeedbackTable from './NegativeFeedbackTable';
import OccupancyBreakdownTable from './OccupancyBreakdownTable';
import TimeSlotsSummaryTable from './TimeSlotsSummaryTable';
import CapacityFormulaSelector from './CapacityFormulaSelector';
import InternalControlTable from './InternalControlTable';
import { useOpsStore } from '../../store/ops.store';

interface Props {
  isDark: boolean;
  theme: Record<string, string>;
}

export default function OpsDashboardContent({ isDark }: Props) {
  const { activeView, isLoading, error, reportFrom, reportTo, fetchedKeys } = useOpsStore();

  const hasFetched = Object.keys(fetchedKeys).length > 0;

  return (
    <>
      <DashboardFilters isDark={isDark} />

      {error && (
        <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      )}

      {!isLoading && !hasFetched && activeView !== 'config' && (
        <div className="mt-8 flex flex-col items-center justify-center py-20 text-center">
          <Play className={`w-12 h-12 ${isDark ? 'text-slate-600' : 'text-gray-400'} mb-4`} />
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chọn khoảng thời gian và nhấn <span className="text-cyan-400 font-medium">"Chạy report"</span> để xem dữ liệu</p>
        </div>
      )}

      {!isLoading && hasFetched && activeView === 'executive' && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            OPS Dashboard <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
          </h2>
          <ScorecardsRow isDark={isDark} />
          <OccupancyBreakdownTable isDark={isDark} />
          <ProductPerformanceChart isDark={isDark} />
        </div>
      )}

      {!isLoading && hasFetched && activeView === 'heatmap' && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Heatmap <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
          </h2>
          <CheckinHeatmap isDark={isDark} />
          <SessionHeatmap isDark={isDark} />
          <CheckinPerSessionHeatmap isDark={isDark} />
        </div>
      )}

      {!isLoading && hasFetched && activeView === 'quality' && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Chất lượng <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
          </h2>
          <RatingDistributionTable isDark={isDark} />
          <NegativeFeedbackTable isDark={isDark} />
        </div>
      )}

      {!isLoading && hasFetched && activeView === 'internal-control' && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <div>
            <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Kiểm soát nội bộ <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
            </h2>
            <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Nhân viên book lịch tập mà không đến
            </p>
          </div>
          <InternalControlTable isDark={isDark} />
        </div>
      )}

      {!isLoading && activeView === 'config' && (
        <div className="mt-4 sm:mt-6 space-y-6">
          <CapacityFormulaSelector isDark={isDark} />
          <TimeSlotsSummaryTable isDark={isDark} />
        </div>
      )}
    </>
  );
}
